import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LogoMarque } from '../marque/LogoMarque';
import { appelApi, ErreurApi } from '../lib/api';
import { useTitrePage } from '../lib/useTitrePage';

export function ReinitialiserMotDePassePage(): JSX.Element {
  useTitrePage('Nouveau mot de passe');
  const [params] = useSearchParams();
  const jeton = params.get('jeton');
  const navigate = useNavigate();

  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    if (!jeton) {
      navigate('/login');
    }
  }, [jeton, navigate]);

  async function soumettre(evenement: React.FormEvent): Promise<void> {
    evenement.preventDefault();
    if (!jeton) return;
    setErreur(null);
    setEnCours(true);
    try {
      await appelApi('/auth/reinitialiser', {
        methode: 'POST',
        corps: { jeton, motDePasse },
      });
      navigate('/login', { replace: true });
    } catch (cause) {
      setErreur(
        cause instanceof ErreurApi
          ? cause.message
          : 'Réinitialisation impossible. Vérifiez votre accès au réseau.',
      );
    } finally {
      setEnCours(false);
    }
  }

  if (!jeton) return <></>;

  return (
    <div className="login-bg flex min-h-screen items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md rounded-3xl bg-white p-4 sm:p-8 shadow-2xl lg:p-10">
        <div className="mb-8 flex items-center justify-center">
          <LogoMarque className="h-10 w-auto" />
        </div>

        <div className="mb-8 text-center">
          <h1 className="font-display text-2xl font-bold text-codialis-navy">Nouveau mot de passe</h1>
          <p className="mt-2 text-sm text-codialis-gray">
            Choisissez un nouveau mot de passe pour votre espace de travail.
          </p>
        </div>

        <form onSubmit={soumettre} className="space-y-6" noValidate>
          <div>
            <label
              htmlFor="motdepasse"
              className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-codialis-navy"
            >
              Nouveau mot de passe
            </label>
            <input
              id="motdepasse"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-codialis-mint"
            />
          </div>

          {erreur && (
            <div
              role="alert"
              className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {erreur}
            </div>
          )}

          <button
            type="submit"
            disabled={enCours || motDePasse.length < 8}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-codialis-navy py-3.5 font-semibold text-white transition hover:bg-codialis-deep disabled:cursor-not-allowed disabled:opacity-60"
          >
            {enCours ? 'Réinitialisation…' : 'Enregistrer'}
          </button>

          <div className="text-center text-sm">
            <Link to="/login" className="text-codialis-sky hover:underline">
              Annuler
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
